import { useEffect } from "react";
import { useAuth } from "../../components/context/AuthContext";
import { showConfetti } from "../../components/elements/ConfettiView";
import { InfoBox } from "../../components/elements/Elements";
import { reloadData } from "../../components/lists/ListHandler";
import { API_LOANS, API_PROFILE_ME } from "../../constants/apis";
import { INFO_YELLOW } from "../../constants/colors";
import {
  COMPONENT,
  INVEST,
  INVESTMENT_PLANS,
  INVESTMENTS,
  TEXT,
} from "../../constants/constants";
import { PropOptions } from "../../utils/formUtils";
import { transactionPinField } from "../../utils/pinUtils";
import { currencyFormat, numberFormat } from "../../utils/helpers";
import useGet from "../common/useGet";
import { TSet } from "../common/useSet";
import {
  CalculatorCard,
  FundSourceCard,
} from "../../components/elements/CardElements";

export const useFundLoanForm = ({ form, state, updateFormValues }: any) => {
  const { user, updateUser } = useAuth();
  const { loadData: getProfile } = useGet();

  const { id: loanId, unit_price, units_available, purpose } = state || {};

  const units_purchased = form?.units_purchased;
  const total_units = units_purchased || 1;
  const investment_amount = units_purchased * unit_price;

  useEffect(() => {
    updateFormValues({ units_purchased: 1 });
  }, []);

  const fields = [
    // Loan context
    {
      type: COMPONENT,
      component: purpose ? (
        <div className="textCenter font13 grey capitalize mb5">
          Investing in: <b>{purpose?.replace(/_/g, " ")}</b>
        </div>
      ) : null,
    },
    {
      field: "units_purchased",
      type: TEXT,
      required: true,
      props: {
        ...PropOptions.number,
        min: 1,
        max: units_available,
        helperText: `Minimum 1 unit · Maximum ${numberFormat(units_available)} units available`,
      },
    },
    {
      type: COMPONENT,
      component: (
        <CalculatorCard
          title="Investment Calculator"
          fields={{
            available_units: numberFormat(units_available),
            purchasing: numberFormat(total_units),
            unit_price: currencyFormat(unit_price),
            total_investment: currencyFormat(investment_amount),
          }}
        />
      ),
    },
    transactionPinField,
    {
      type: "component",
      component: <FundSourceCard />,
    },
    {
      type: COMPONENT,
      component: (
        <InfoBox color={INFO_YELLOW}>
          ⚠️ <b>Investment Risk</b>: This investment is not FDIC insured and
          involves risk of loss. Past performance does not guarantee future
          results. Your capital is at risk.
        </InfoBox>
      ),
    },
  ];

  const onSuccess = () => {
    // Reload affected lists
    reloadData(INVESTMENT_PLANS);
    reloadData(INVESTMENTS);
    showConfetti();

    // Refresh user profile to capture updated balances
    getProfile({
      api: API_PROFILE_ME,
      onSuccess: (data: any) => {
        updateUser({ ...user, ...data });
      },
    });
  };

  const onSubmit = ({ payload }: any): TSet => {
    const api = `${API_LOANS}${loanId}/fund/`;

    return {
      api,
      params: payload,
      successMessage: { message: "Loan funded successfully! 🎉", duration: 7 },
      onSuccess,
      method: "PATCH",
    };
  };

  const allowSubmit =
    parseInt(units_purchased) >= 1 &&
    parseInt(units_purchased) <= units_available;

  return {
    fields,
    onSubmit,
    submitText: `${INVEST} ${currencyFormat(investment_amount)}`,
    allowSubmit,
  };
};